import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetNewAndOfferListItemResponseDtoListResponseDto, NewsAndOffersStatus, NewsAndOffersType } from '@/features/news-and-offers/types/news-and-offers-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    take?: number;
    skip?: number;
    startDate?: Date;
    endDate?: Date;
    types?: NewsAndOffersType[];
    statuses?: NewsAndOffersStatus[];
} & ApiHookProps;

export const useNewsAndOffersListApi = ({searchText, take, skip, startDate, endDate, types, statuses }: Props = {}) => {
    const axiosInstance = useAxiosAuth();

    const {
        data: newsAndOffersListData, 
        isLoading: isLoadingNewsAndOffersListData, 
        isError: isErrorNewsAndOffersListData,
        fetchNextPage: fetchNewsAndOffersListDataNextPage,
        hasNextPage: hasNewsAndOffersListDataNextPage,
        isFetchingNextPage: isFetchingNewsAndOffersListDataNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.newsAndOffers.queryKeys.list, 
            searchText, take, skip, startDate, endDate, types, statuses
        ],
        queryFn: async ({pageParam}): Promise<GetNewAndOfferListItemResponseDtoListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.newsAndOffers.endpoints.list, {  
                startDate: DateTimeHelper.getDateOnly(startDate), 
                endDate: DateTimeHelper.getDateOnly(endDate), 
                skip: pageParam, 
                searchText, take, types, statuses
            });
            const response = await axiosInstance.get<GetNewAndOfferListItemResponseDtoListResponseDto>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedData = newsAndOffersListData?.pages.flatMap((page) => page.items ?? []);

    return {
        newsAndOffersListData: fetchedData,
        isLoadingNewsAndOffersListData,
        isErrorNewsAndOffersListData,
        fetchNewsAndOffersListDataNextPage,
        hasNewsAndOffersListDataNextPage,
        isFetchingNewsAndOffersListDataNextPage
    }
};
